// Nghiệp vụ thuần (không phụ thuộc React/store) của module Tài liệu — có unit test kèm theo
import dayjs from 'dayjs';
import { ALLOWED_EXT, MAX_TOTAL_SIZE, CODE_SUFFIX, type Doc, type DocCategory, type DocState, type Employee } from './data';

export const todayStr = () => dayjs().format('YYYY-MM-DD');

/** Trạng thái hiển thị của tài liệu: kết hợp trạng thái duyệt + ngày hiệu lực + ban hành lại */
export function docState(d: Doc, today = todayStr()): DocState {
  if (d.status !== 'published') return d.status;
  if (d.replacedById) return 'replaced';
  if (d.effectiveFrom > today) return 'upcoming';
  if (d.effectiveTo && d.effectiveTo < today) return 'expired';
  return 'active';
}

/** Danh sách người nhận (không gồm người tạo): toàn công ty / cùng phòng với người tạo / chọn tay */
export function recipientIds(d: Pick<Doc, 'shareAll' | 'internalShare' | 'sharedUserIds' | 'createdBy'>, people: Employee[]): string[] {
  if (d.shareAll) return people.filter(p => p.id !== d.createdBy).map(p => p.id);
  const ids = new Set(d.sharedUserIds);
  if (d.internalShare) {
    const dept = people.find(p => p.id === d.createdBy)?.deptId;
    people.forEach(p => { if (dept && p.deptId === dept) ids.add(p.id); });
  }
  ids.delete(d.createdBy);
  return people.filter(p => ids.has(p.id)).map(p => p.id);
}

export interface Viewer { id: string; manage: boolean; approve: boolean }

/**
 * Quyền xem 1 tài liệu:
 * - Người quản lý (document.edit) / người duyệt: xem mọi tài liệu chưa xóa.
 * - Người tạo: luôn xem được tài liệu của mình.
 * - Người được chia sẻ: xem tài liệu đã ban hành / đã thu hồi, và bản dự thảo đang mở góp ý.
 */
export function canViewDoc(d: Doc, viewer: Viewer, people: Employee[]): boolean {
  if (d.deletedAt) return viewer.manage;
  if (viewer.manage || viewer.approve || d.createdBy === viewer.id) return true;
  const shared = recipientIds(d, people).includes(viewer.id);
  if (!shared) return false;
  return d.status === 'published' || d.status === 'revoked' || (d.status === 'draft' && d.draftMode);
}

/** Sửa nội dung: chỉ khi chưa ban hành. Tài liệu đã ban hành chỉ được sửa phân quyền chia sẻ. */
export const isContentEditable = (d: Doc) => d.status === 'draft' || d.status === 'rejected' || d.status === 'pending';
/** Xóa: không xóa tài liệu đang/ sắp có hiệu lực — phải thu hồi trước */
export const isDeletable = (d: Doc) => !['active', 'upcoming'].includes(docState(d));

/** Mã tài liệu kế tiếp theo ký hiệu loại + năm: 007/2026/QĐ-KHOTHI */
export function nextDocCode(docs: Pick<Doc, 'code'>[], prefix: string, year = dayjs().year()): string {
  const tail = `/${year}/${prefix}-${CODE_SUFFIX}`;
  const max = docs.reduce((m, d) => {
    if (!d.code.endsWith(tail)) return m;
    const n = parseInt(d.code.slice(0, d.code.length - tail.length), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return `${String(max + 1).padStart(3, '0')}${tail}`;
}

/** Mã loại kế tiếp G0009 */
export function nextTypeCode(codes: string[]): string {
  const max = codes.reduce((m, c) => {
    const n = /^G(\d+)$/i.test(c) ? parseInt(c.slice(1), 10) : 0;
    return n > m ? n : m;
  }, 0);
  return `G${String(max + 1).padStart(4, '0')}`;
}

/** Mã danh mục kế tiếp: gốc DM09, con DM02.4 */
export function nextCategoryCode(categories: DocCategory[], parentId: string | null): string {
  if (parentId) {
    const parent = categories.find(c => c.id === parentId);
    const prefix = `${parent?.code || 'DM'}.`;
    const max = categories.filter(c => c.parentId === parentId).reduce((m, c) => {
      const n = c.code.startsWith(prefix) ? parseInt(c.code.slice(prefix.length), 10) : 0;
      return Number.isFinite(n) && n > m ? n : m;
    }, 0);
    return `${prefix}${max + 1}`;
  }
  const max = categories.filter(c => !c.parentId).reduce((m, c) => {
    const n = /^DM(\d+)$/i.test(c.code) ? parseInt(c.code.slice(2), 10) : 0;
    return n > m ? n : m;
  }, 0);
  return `DM${String(max + 1).padStart(2, '0')}`;
}

export function descendantIds(categories: DocCategory[], id: string): string[] {
  const out: string[] = [];
  const walk = (pid: string) => categories.filter(c => c.parentId === pid).forEach(c => { out.push(c.id); walk(c.id); });
  walk(id);
  return out;
}

export function categoryDepth(categories: DocCategory[], id: string | null): number {
  const byId = new Map(categories.map(c => [c.id, c]));
  let depth = 0;
  let cur = id ? byId.get(id) : undefined;
  while (cur) { depth++; cur = cur.parentId ? byId.get(cur.parentId) : undefined; }
  return depth;
}

export function categoryPath(categories: DocCategory[], id: string): string {
  const byId = new Map(categories.map(c => [c.id, c]));
  const names: string[] = [];
  let cur = byId.get(id);
  while (cur) { names.unshift(cur.name); cur = cur.parentId ? byId.get(cur.parentId) : undefined; }
  return names.join(' / ') || '—';
}

export interface CategoryNode extends DocCategory { children?: CategoryNode[] }

/** Dựng cây danh mục đã sắp xếp theo `order`; `keep` lọc node (node cha được giữ nếu có con khớp) */
export function buildCategoryTree(categories: DocCategory[], keep?: (c: DocCategory) => boolean): CategoryNode[] {
  const build = (pid: string | null): CategoryNode[] =>
    categories
      .filter(c => c.parentId === pid)
      .sort((a, b) => a.order - b.order)
      .map(c => {
        const children = build(c.id);
        const node: CategoryNode = { ...c, children: children.length ? children : undefined };
        return node;
      })
      .filter(n => !keep || keep(n) || !!n.children?.length);
  return build(null);
}

export const fileExt = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');

export const fmtSize = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/** Kiểm tra file tải lên: định dạng cho phép, không trùng tên, tổng dung lượng ≤ 250MB */
export function validateUploads(existing: { name: string; size: number }[], incoming: { name: string; size: number }[]) {
  const errors: string[] = [];
  const accepted: number[] = [];
  let total = existing.reduce((s, f) => s + f.size, 0);
  const names = new Set(existing.map(f => f.name.toLowerCase()));
  incoming.forEach((f, idx) => {
    const ext = fileExt(f.name);
    if (!ALLOWED_EXT.includes(ext)) return errors.push(`${f.name}: định dạng .${ext || '?'} không được hỗ trợ`);
    if (f.size === 0) return errors.push(`${f.name}: file rỗng`);
    if (names.has(f.name.toLowerCase())) return errors.push(`${f.name}: đã có file trùng tên`);
    if (total + f.size > MAX_TOTAL_SIZE) return errors.push(`${f.name}: vượt tổng dung lượng tối đa ${fmtSize(MAX_TOTAL_SIZE)}`);
    total += f.size;
    names.add(f.name.toLowerCase());
    accepted.push(idx);
  });
  return { accepted, errors };
}

/** So khớp không dấu, không phân biệt hoa thường */
export const normalize = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().trim();
export const matches = (q: string, ...fields: (string | undefined)[]) => {
  const n = normalize(q);
  return !n || fields.some(f => f && normalize(f).includes(n));
};
